import React from "react";

// material-ui
import Collapse from "@mui/material/Collapse";
import Fade from "@mui/material/Fade";
import Grow from "@mui/material/Grow";
import Slide from "@mui/material/Slide";
import type { SlideProps } from "@mui/material/Slide";
import Zoom from "@mui/material/Zoom";
import type { ZoomProps } from "@mui/material/Zoom";
import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";

// ==============================|| TYPES ||============================== //

type PositionType =
  | "top-left"
  | "top-right"
  | "top"
  | "bottom-left"
  | "bottom-right"
  | "bottom";

type TransitionType = "grow" | "collapse" | "fade" | "slide" | "zoom";

type DirectionType = "up" | "right" | "left" | "down";

interface PositionStyle {
  transformOrigin: string;
}

interface TransitionsProps {
  children: React.ReactNode;
  position?: PositionType;
  type?: TransitionType;
  direction?: DirectionType;
  ref?: React.Ref<HTMLDivElement>;
  [key: string]: unknown;
}

interface PopupTransitionProps extends ZoomProps {
  ref?: React.Ref<unknown>;
}

// ==============================|| TRANSITIONS - COMPONENT ||============================== //

export default function Transitions({
  children,
  position = "top-left",
  type = "grow",
  direction = "up",
  ref,
  ...others
}: TransitionsProps): React.ReactElement {
  let positionSX: PositionStyle = {
    transformOrigin: "0 0 0",
  };

  switch (position) {
    case "top-right":
      positionSX = {
        transformOrigin: "top right",
      };
      break;
    case "top":
      positionSX = {
        transformOrigin: "top",
      };
      break;
    case "bottom-left":
      positionSX = {
        transformOrigin: "bottom left",
      };
      break;
    case "bottom-right":
      positionSX = {
        transformOrigin: "bottom right",
      };
      break;
    case "bottom":
      positionSX = {
        transformOrigin: "bottom",
      };
      break;
    case "top-left":
    default:
      positionSX = {
        transformOrigin: "0 0 0",
      };
      break;
  }

  return (
    <Box ref={ref}>
      {type === "grow" && (
        <Grow
          {...others}
          timeout={{
            appear: 0,
            enter: 150,
            exit: 150,
          }}
        >
          <Box sx={positionSX as SxProps<Theme>}>{children}</Box>
        </Grow>
      )}

      {type === "collapse" && (
        <Collapse {...others} sx={positionSX as SxProps<Theme>}>
          {children}
        </Collapse>
      )}

      {type === "fade" && (
        <Fade
          {...others}
          timeout={{
            appear: 0,
            enter: 300,
            exit: 150,
          }}
        >
          <Box sx={positionSX as SxProps<Theme>}>{children}</Box>
        </Fade>
      )}

      {type === "slide" && (
        <Slide
          {...(others as Omit<SlideProps, "direction">)}
          timeout={{
            appear: 0,
            enter: 150,
            exit: 150,
          }}
          direction={direction}
        >
          <Box sx={positionSX as SxProps<Theme>}>{children}</Box>
        </Slide>
      )}

      {type === "zoom" && (
        <Zoom {...others}>
          <Box sx={positionSX as SxProps<Theme>}>{children}</Box>
        </Zoom>
      )}
    </Box>
  );
}

// ==============================|| POPUP TRANSITION - COMPONENT ||============================== //

export function PopupTransition({
  ref,
  ...props
}: PopupTransitionProps): React.ReactElement {
  return <Zoom ref={ref} timeout={200} {...props} />;
}
